import type { ReactNode } from 'react';
import { externalLinkAttrs, sanitizeHref } from '../../lib/url';
import { usePendingLink } from './ComingSoon';

export type ButtonVariant =
  /** Azul sólido sobre fondo claro — acción principal. */
  | 'primary'
  /** Contorno gris sobre fondo claro — acción secundaria. */
  | 'outline'
  /** Blanco sólido sobre fondo azul — acción principal invertida. */
  | 'inverse'
  /** Contorno translúcido sobre fondo azul u oscuro. */
  | 'ghost';

interface ButtonProps {
  readonly href: string;
  readonly children: ReactNode;
  readonly variant?: ButtonVariant;
  /** Segunda línea, más pequeña (los CTA duales del hero). */
  readonly hint?: string;
  readonly className?: string;
  /**
   * Nombre de la acción para el aviso de «en desarrollo». Sólo hace falta
   * cuando `children` no es texto plano.
   */
  readonly label?: string;
  /** Se ejecuta al activar el botón, navegue o no (cerrar el menú móvil). */
  readonly onActivate?: () => void;
}

const BASE =
  'inline-block rounded-btn font-display font-semibold tracking-[0.02em] transition-[background-color,color,border-color,transform,box-shadow] duration-250 will-change-transform';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-brand text-white shadow-btn hover:bg-brand-hover hover:-translate-y-px',
  outline:
    'border border-[#c6cedb] text-ink hover:border-brand hover:text-brand hover:-translate-y-px',
  inverse: 'bg-white text-brand hover:bg-[#eef3fb] hover:-translate-y-px',
  ghost:
    'border border-white/45 text-white hover:border-white hover:bg-white/10 hover:-translate-y-px',
};

/**
 * Todos los CTA del sitio. Renderiza un `<a>`, nunca un `<button>`, porque cada
 * uno navega — un `<button>` aquí rompería abrir en pestaña nueva y la
 * semántica para lectores de pantalla.
 *
 * El href pasa siempre por `sanitizeHref`, y los destinos externos reciben
 * `rel="noopener noreferrer"` automáticamente. Es imposible saltarse ambas
 * cosas desde un componente hijo, que es justamente el objetivo.
 *
 * Mientras el destino figure como pendiente, el clic muestra el aviso de «en
 * desarrollo» en lugar de navegar. El `href` se conserva de todas formas: es lo
 * que hace que el enlace siga siendo un enlace para el navegador y para un
 * lector de pantalla, y lo que hará que funcione el día que la ruta exista.
 */
export function Button({
  href,
  children,
  variant = 'primary',
  hint,
  className = '',
  label,
  onActivate,
}: ButtonProps) {
  const safeHref = sanitizeHref(href);
  const pendingLink = usePendingLink();
  const action = label ?? (typeof children === 'string' ? children : href);
  const { onClick: onPendingClick } = pendingLink(href, action);

  return (
    <a
      href={safeHref}
      {...externalLinkAttrs(safeHref)}
      onClick={(event) => {
        onActivate?.();
        onPendingClick?.(event);
      }}
      className={`${BASE} ${VARIANTS[variant]} ${
        hint ? 'px-6 py-3 text-left' : 'px-[26px] py-[13px] text-sm'
      } ${className}`}
    >
      {hint ? (
        <>
          <span className="block text-base leading-tight">{children}</span>
          <span className="mt-0.5 block text-[12.5px] font-normal opacity-75">
            {hint}
          </span>
        </>
      ) : (
        children
      )}
    </a>
  );
}
